<template>
  <div class="table-container">
    <CustomColumnsMenu v-if="isCustomColumn" />
    <v-responsive>
      <v-data-table
        :mobile-breakpoint="0"
        :items="getItems"
        v-model="selected"
        :headers="selectedColumns"
        :options="options"
        :footer-props="{ 'items-per-page-options': paginationRows }"
        item-key="index"
        :class="`elevation-1  ${!showFooter ? 'hide-footer' : ''}`"
        v-bind="resetProps"
        :v-on="$listeners"
        :hide-default-header="hideDefaultHeader"
        :hide-default-footer="true"
        :showCreate="showCreate"
        :dense="false"
        :fix-header="true"
        :single-select="true"
        :loading="loading"
        @toggle-select-all="handleOnToggleSelectAll"
        @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
        @update:options="handleOnUpdateOptionsDebounce"
      >
        <!-- Table Top override position -->
        <template v-slot:top="{ items }"></template>

        <!-- Table Header override -->
        <template v-slot:header="props">
          <slot name="header" v-bind="{ ...props }" :columns="selectedColumns" />
        </template>

        <!-- Table Body override -->
        <template v-slot:body="{ items, isSelected }">
          <TBody
            :items="items"
            :headers="selectedColumns"
            :hideDots="hideDots"
            :showCreate="showCreate"
            :handleOnCreateNew="handleOnCreateNew"
            :loading="loading"
            :expandFirst="expandFirst"
          >
            <template v-slot:no-data>
              <slot v-if="!loading" name="no-data" />
            </template>

            <!-- item slot-->
            <!-- <template v-slot:item-${header.value}="props" />  -->
            <template v-for="head in selectedColumns" v-slot:[`item-${head.value}`]="props">
              <slot :name="`item-${head.value}`" v-show="!!head.selected" v-bind="{ ...props }" />
            </template>

            <!-- New record row-->
            <!-- <template v-slot:newRecord="props" />  -->
            <template v-slot:newRecord="props">
              <slot name="newRecord" v-bind="{ ...props }" />
            </template>

            <!-- Save new row button-->
            <!-- <template v-slot:saveNewBtn="props" />  -->
            <template v-slot:saveNewBtn="{ _class }">
              <slot name="saveNewBtn" :_class="_class" />
            </template>

            <!-- Expand row-->
            <!-- <template v-slot:expand="props" />  -->
            <template v-slot:expand="props">
              <slot name="expand" v-bind="{ ...props }" />
            </template>
          </TBody>
        </template>
        <!-- Table Footer override -->
        <template v-slot:footer="{ props }" v-if="!!showFooter">
          <AirTableFooter
            :loading="loading"
            v-bind="{ ...props }"
            :totalRows="totalRows"
            :handleUpdateTableData="handleOnUpdateFooterTableOptions"
          />
        </template>
      </v-data-table>
    </v-responsive>
  </div>
</template>

<script>
import Vue from "vue";
import { AirTableFooter, CustomColumnsMenu } from "@/components";
import { mapGetters } from "vuex";
import { TABLE_SORT } from "@/enums/apps.enum";

export default Vue.extend({
  name: "AirTable",

  data() {
    return {
      expanded: [],
      singleExpand: false,
      originalHeaders: [],
      selected: [],
      selectedAll: false,
      currentFilter: null,
      isReady: false,
    };
  },

  components: { CustomColumnsMenu, AirTableFooter },

  props: {
    headers: {
      default: () => [],
      type: Array,
    },
    items: {
      default: () => [],
      type: Array,
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    options: {
      pageStart: 200,
    },
    handleUpdateTableData: {
      type: Function,
      default: () => {},
    },
    handleOnCreateNew: {
      type: Function,
      default: null,
    },
    toggleSelectAll: {
      type: Function,
      default: null,
    },
    totalRows: {
      type: [Number, String],
      default: 0,
    },
    showCreate: {
      type: Boolean,
      default: false,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    hideDots: {
      type: Boolean,
      default: false,
    },
    modelName: {
      type: String,
      default: "",
    },
    hideDefaultHeader: {
      type: Boolean,
      default: false,
    },
    isFilter: {
      default: true,
      type: Boolean,
    },
    isCustomColumn: {
      default: true,
      type: Boolean,
    },

    hideSelect: {
      default: true,
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },

    expandFirst: {
      default: false,
      type: Boolean,
    },
    paginationRows: {
      type: Array,
      default: () => [10, 32, 80, 100],
    },
    isApiSort: {
      type: Boolean,
      default: false,
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.currentFilter = this.filter;
      this.initHeaders();
      this.isReady = true;

    });
  },

  computed: {
    ...mapGetters({
      filter: "App/filter",
    }),

    /**
     * Delegate all attributes (other props)
     * @return {any}
     */
    resetProps() {
      return { ...this.$attrs };
    },

    /**
     * Get all items
     * @return {Array<any>}
     */
    getItems() {
      return this.items;
    },

    /**
     * Get selected columns
     * @return {Array<FilterColumnInterface>}
     */
    selectedColumns() {
      if(!this.hideDots && !this.isFilter){
         const prependActions = {
        align: "left",
        class: "col-actions",
        sortable: false,
        value: "prepend_dot",
        edge: 1,
      };

      const appendActions = {
        align: "left",
        class: "col-actions",
        sortable: false,
        value: "append_dot",
        edge: 2,
      };

      return [appendActions,...this.headers,prependActions]

      }
      
      return !!this.isFilter ? this.currentFilter?.selectedColumns() : [...this.headers];

      // console.log("headers", this.headers);
      // return this.headers;
    },
  },

  methods: {
    /***
     * Init header columns & set selected columns
     */
    async initHeaders() {
      if (this.hideDots) {
        return;
      }

      const prependActions = {
        align: "left",
        class: "col-actions",
        sortable: false,
        value: "prepend_dot",
        edge: 1,
      };

      const appendActions = {
        align: "left",
        class: "col-actions",
        sortable: false,
        value: "append_dot",
        edge: 2,
      };
      if (this.isFilter) {
        await this.currentFilter?.setNewColumn(prependActions, 0);
        await this.currentFilter?.setNewColumn(appendActions, this.currentFilter.columns.length);
      } else {
        // this.headers.splice(0, 0, { ...prependActions });
        // this.headers.push(appendActions);
      }
    },

    /**
     * Function to toggle select all
     * @return {void}
     */
    handleOnToggleSelectAll(value) {
      if (this.toggleSelectAll) {
        this.toggleSelectAll(value);
      }
    },

    /**
     * will check if sortable and call handle update table data if it is , return if false.
     * @param options
     */
    handleOnUpdateTableOptions(options) {
      if (!this.isReady || !this.isApiSort) {
        return;
      }

      options = {
        ...options,
        sort: this.getSortOptions(options),
      };
      this.handleUpdateTableData(options);
    },

    handleOnUpdateOptionsDebounce: _.debounce(function (options) {
      return this.handleOnUpdateTableOptions(options);
    }, 900),

    /***
     * Handle on update footer options
     * @param options
     */
    handleOnUpdateFooterTableOptions(options) {
      options = {
        ...options,
        sort: this.getSortOptions(options),
      };
      this.handleUpdateTableData(options);
    },

    /***
     * Handle on getting the prepared sort options
     * @param options
     * @return {string}
     */
    getSortOptions(options) {
      const alias = TABLE_SORT[`${options?.sortDesc?.[0] || false}`];
      const sort = options?.sortBy?.[0] || "";
      return `${alias}${sort}`;
    },
  },
});
</script>

<style scoped lang="scss" src="./styles.scss" />
